import { ReactElement, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Vaccine } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Vaccines";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CheckboxLabel, LinkButton } from "@/DesignSystem";
import { useCommand } from "@/Flowsheets/Hooks/useCommand";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";

import styles from "./AddImmunizationPopup.module.scss";

interface AddImmunizationPopupProps {
    patientId: string;
    encounterId: Guid;
    encounterDate: Date;
    onClose: () => void;
    onSave: () => void;
}

function AddImmunizationPopup(props: Readonly<AddImmunizationPopupProps>): ReactElement {
    const { getText } = useTranslation();

    const [dateTextValue, setDateTextValue] = useState<string>("");
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const immunizationOptions = useMemo(
        () => Immutable.List(cacheStore.VaccinesCache.map(x => new SearchableItemModelT<Vaccine>(Vaccine.GetLocalizedName(x), x.Id.toString(), x))),
        []
    );
    const [immunizationName, setImmunizationName] = useState<string>("");
    const [immunizationAgent, setImmunizationAgent] = useState<string>("");
    const [isRefused, setIsRefused] = useState<boolean>(false);
    const [isDateApproximate, setIsDateApproximate] = useState<boolean>(false);
    const isImmunizationNameValid = immunizationName.length > 0;
    const isImmunizationAgentValid = immunizationAgent.length > 0;

    function onChangeDate(dateWithPrecision: DateTimeWithPrecision): void {
        setDateTextValue(dateWithPrecision.text);

        if (dateWithPrecision.status !== LifeStageDateStatusEnum.Valid && !dateWithPrecision.isDayPrecision) {
            setDateValue(null);
        }
        else {
            setDateValue(dateWithPrecision.value);
        }
    }

    async function saveNewImmunization(): Promise<ClientResponse<{
        MedicalItems: MedicalItemImmunization[];
    }>> {
        const newMedicalItem: MedicalItemImmunization = MedicalItemImmunization.newImmunization(props.encounterId);

        if (isRefused) {
            newMedicalItem.RefusedDate = dateValue;
            newMedicalItem.RefusedPrecisionDate = getDatePrecision();
        }
        else {
            newMedicalItem.AdministeredDate = dateValue;
            newMedicalItem.AdministeredPrecisionDate = getDatePrecision();
        }

        newMedicalItem.ConsultationDate = props.encounterDate;
        newMedicalItem.Name = immunizationName;
        newMedicalItem.Type = immunizationAgent;
        newMedicalItem.IsRefused = isRefused;

        const patientModel = patientStore.PatientModels[props.patientId];

        const requestData = {
            PatientContactId: props.patientId,
            PatientContactFacetId: patientModel.PatientResponse.ContactFacet_id,
            MedicalItems: [newMedicalItem],
            SaveInNewEncounter: false,
            LatestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees: null
        };

        return await Client.postAsync<{ MedicalItems: MedicalItemImmunization[] }>(
            ServerOperations.Server.Myle_Patient_MedicalItem_SaveMedicalItemImmunization,
            requestData
        );
    }

    const { mutate: saveNewImmunizationCommand, isLoading } = useCommand(saveNewImmunization, {
        onSuccess(response: ClientResponse<{
            MedicalItems: MedicalItemImmunization[];
        }>
        ): void {
            if (response.isSuccess) {
                sessionStore.dispatch(
                    LocalPatientOperations.MedicalItems.SaveImmunizations,
                    {
                        immunizations: response.data.MedicalItems
                    }
                );

                props.onSave();
            }
        }
    });

    const isSaveDisabled = !isImmunizationAgentValid || !isImmunizationNameValid || !dateValue || isLoading;

    function getDatePrecision(): LifeStageDatePrecisionEnum {
        return isDateApproximate ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;
    }

    function getEncounterDateTimeWithPrecision(): DateTimeWithPrecision {
        return new DateTimeWithPrecision(
            LuxonDateTimeParser.parse(props.encounterDate).toFormat("yyyy-MM-dd"),
            dateTextValue,
            getDatePrecision()
        );
    }

    function handleOnTodayClick(): void {
        onChangeDate(getEncounterDateTimeWithPrecision());
    }

    function handleApproximateCheckboxChange(isChecked: boolean): void {
        setIsDateApproximate(isChecked);

        if (dateValue) {
            onChangeDate(new DateTimeWithPrecision(
                LuxonDateTimeParser.parse(dateValue).toFormat("yyyy-MM-dd"),
                dateTextValue,
                getDatePrecision()
            ));
        }
    }

    return createPortal(
        <Modal
            title={getText("AddImmunizationPopup_Title")}
            onClose={props.onClose}
            showX
            width={650}
            footer={(
                <div className={styles.footer}>
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={props.onClose}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            disabled={isSaveDisabled}
                            onClick={saveNewImmunizationCommand}
                            label={getText("SaveLabel")}
                        />
                    </ButtonGroup>
                </div>
            )}
            showOverlay
            disableAutoScroll
        >
            <div className={styles.container}>
                <div className={styles.gridContainer}>
                    <label
                        className={styles.label}
                        htmlFor="add-immunization-popup-immunization-name-input"
                    >
                        {getText("ImmunizationNameDLabel")}
                    </label>
                    <TextboxControl
                        id="add-immunization-popup-immunization-name-input"
                        onMountFocus
                        valid={isImmunizationNameValid}
                        onChange={setImmunizationName}
                        onAutoCompleteSelectCallBack={(_, model: Vaccine, value) => {
                            setImmunizationName(value);
                            const agent = Vaccine.GetLocalizedImmunogen(model);
                            if (agent)
                                setImmunizationAgent(agent);

                            if (!dateValue)
                                handleOnTodayClick();
                        }}
                        autoCompleteItems={immunizationOptions}
                        value={immunizationName}
                    />
                    <label
                        className={styles.label}
                        htmlFor="add-immunization-popup-immunization-agent-input"
                    >
                        {getText("QuebecVaccinationRegistry_ImmunizingAgentLabel")}
                    </label>
                    <TextboxControl
                        id="add-immunization-popup-immunization-agent-input"
                        valid={isImmunizationAgentValid}
                        value={immunizationAgent}
                        onChange={setImmunizationAgent}
                    />
                    <div />
                    <div className={styles.refusedDateContainer}>
                        <CheckboxLabel
                            id="add-immunization-popup-immunization-refused-checkbox"
                            checked={isRefused}
                            onChange={setIsRefused}
                            label={getText("ImmunizationViewRefusedLabel")}
                        />
                    </div>

                    <label
                        className={styles.label}
                        htmlFor="txtdtadd-immunization-popup-date"
                    >
                        {isRefused ? getText("RefusedDateDLabel") : getText("AdministeredDateDLabel")}
                    </label>

                    <div className={styles.datePickerContainer}>
                        <TextboxControlWithDatePicker
                            id="add-immunization-popup-date"
                            expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                            isInvalid={!dateValue}
                            textValue={dateTextValue}
                            dateValue={dateValue}
                            onChange={onChangeDate}
                        />
                        <CheckboxLabel
                            id="add-immunization-popup-immunization-approx-checkbox"
                            checked={isDateApproximate}
                            onChange={handleApproximateCheckboxChange}
                            label={getText("ApproxLabel")}
                        />
                        <LinkButton
                            onClick={handleOnTodayClick}
                            underline={false}
                            label={getText("TodayLabel")}
                        />
                    </div>
                </div>
            </div>
        </Modal>,
        document.body
    );
}

export { AddImmunizationPopup };
